import type {
  LimitOrderAmendRequest,
  LimitOrderReferentialFormResponse,
  LimitOrderReloadFormRequest,
  LimitOrderResponse,
} from 'apiConnector/secondary-market/limit-order/models';
import type { ReloadFormResponse } from 'apiConnector/secondary-market/shared/form';
import type {
  SecondaryPostEventsLimitOrderPersistedTabContent,
  SecondaryQuotesLimitOrderFormPersistedTabContent,
} from 'apiConnector/tabs/secondaryMarket.tabs.model';
import { isSecondaryMarketQuotesLimitOrderPersistedTabContent } from 'apiConnector/tabs/secondaryMarket.tabs.model';
import { limitOrderInputFormActions } from 'state/secondaryMarket/limit-order/form/input/inputForm.actions';
import { limitOrderInputFormSelectors } from 'state/secondaryMarket/limit-order/form/input/inputForm.selectors';
import { limitOrderOriginalFormActions } from 'state/secondaryMarket/limit-order/form/original/original.actions';
import { limitOrderOriginalFormSelectors } from 'state/secondaryMarket/limit-order/form/original/original.selector';
import { limitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import { limitOrderOrderActions } from 'state/secondaryMarket/limit-order/order/order.action';
import { secondaryMarketThunksFactory, tabCreatedThunksFactory } from 'state/secondaryMarket/shared/thunk';
import type { TradeSizeInputMode } from 'state/secondaryMarket/shared/tradeInformation.models';
import { computeCashAmount } from 'state/secondaryMarket/shared/utils';
import { formatByPriceType, toBcsProductKey } from 'state/secondaryMarket/utils';
import {
  fromSecondaryPostEventsWorkspace,
  getUserPreferencesSecondaryMarketCloseTabWhenOrderPlacedActivated,
} from 'state/selectors';
import type { Thunk, ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { SecondaryWorkspaceType } from 'state/workspaces/model';
import { workspaceTabFrozen, workspaceTabRemoved, workspaceTabUnactivated } from 'state/workspaces/workspace.actions';
import { getTabWorkspaceType } from 'state/workspaces/workspaces/workspaces.selector';

import { limitOrderAmendActions } from './amend/amend.actions';

const thunks = secondaryMarketThunksFactory<
  LimitOrderAmendRequest,
  LimitOrderResponse,
  LimitOrderReferentialFormResponse,
  LimitOrderReloadFormRequest,
  SecondaryQuotesLimitOrderFormPersistedTabContent | SecondaryPostEventsLimitOrderPersistedTabContent
>('LIMIT_ORDER', 'limitOrder', limitOrderSelectors);

export function amendRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const etag = limitOrderOriginalFormSelectors.getEtag(state, tabId);
    const request = limitOrderSelectors.getAmendRequest(state, tabId);
    const orderId = fromSecondaryPostEventsWorkspace.getOrderId(state, tabId);
    if (etag !== undefined) {
      dispatch(limitOrderAmendActions.secondaryMarketAmendRequested(tabId, orderId, etag, request));
      dispatch(workspaceTabFrozen(tabId, 'secondaryMarketPostEvents', true));
    }
  };
}

export function reloadTabThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const tabContent = limitOrderInputFormSelectors.getFormTabContentToPersist(state, tabId);
    if (!tabContent || !isSecondaryMarketQuotesLimitOrderPersistedTabContent(tabContent)) {
      return;
    }

    const request: LimitOrderReloadFormRequest = {
      productKeyRequest: toBcsProductKey(tabContent?.bcsProductKey),
      clientType: tabContent.clientType,
      client: tabContent.client,
      custodian: tabContent.custodian,
      settlementDate: tabContent.settlementDate,
      salesId: tabContent.salesId,
    };
    dispatch(limitOrderInputFormActions.reloadFormFetchRequested(tabId, 'secondaryMarketQuotes', request));
  };
}
export function amendFetchedThunk(tabId: string, tradeId: string, response: LimitOrderResponse): Thunk<void> {
  return (dispatch, getState) => {
    response = {
      ...response,
      clientLimit: formatByPriceType(response.clientLimit, response.priceType),
      salesMarginAdjusted: formatByPriceType(response.salesMarginAdjusted, response.priceType),
    };
    dispatch(limitOrderOriginalFormActions.secondaryMarketAmendFetched(tabId, tradeId, response));

    const state = getState();
    const originalClient = limitOrderOriginalFormSelectors.getFormClient(state, tabId);
    const originalClientType = limitOrderOriginalFormSelectors.getFormClientType(state, tabId);
    const loadReferentialRequest: LimitOrderReloadFormRequest = {
      productKeyRequest: response.bcsKey,
      clientType: originalClientType,
      client: originalClient,
      custodian: limitOrderOriginalFormSelectors.getFormCustodian(state, tabId),
      salesId: limitOrderOriginalFormSelectors.getFormSalesId(state, tabId),
      settlementDate: limitOrderOriginalFormSelectors.getFormSettlementDate(state, tabId),
      tradeWay: limitOrderOriginalFormSelectors.getFormTradeWay(state, tabId),
    };
    dispatch(
      limitOrderInputFormActions.reloadFormFetchRequested(tabId, 'secondaryMarketPostEvents', loadReferentialRequest),
    );
  };
}
function resetDefaultedFieldOnEmptyClientLimit(dispatch: ThunkDispatch, inputMode: TradeSizeInputMode, tabId: string) {
  if (inputMode === 'amount') {
    dispatch(limitOrderInputFormActions.formFieldChanged(tabId, 'quantity', undefined));
  } else if (inputMode === 'quantity') {
    dispatch(limitOrderInputFormActions.formFieldChanged(tabId, 'cashAmount', undefined));
  }
}
function computDefaultedFieldByInputMode(
  dispatch: ThunkDispatch,
  inputMode: TradeSizeInputMode,
  tabId: string,
  state: AppState,
) {
  if (inputMode === 'amount') {
    dispatch(thunks.defaultQuantityFromCashAmountThunk(tabId, limitOrderSelectors.getFormCashAmount(state, tabId)));
  } else if (inputMode === 'quantity') {
    dispatch(thunks.defaultCashAmountFromQuantityThunk(tabId, limitOrderSelectors.getFormQuantity(state, tabId)));
  }
}
export function clientLimitChangedThunks(tabId: string, clientLimit?: number): Thunk<void> {
  return (dispatch, getState) => {
    thunks.resetSidePanelIfNeeded(dispatch, getState, tabId);
    const state = getState();
    dispatch(limitOrderInputFormActions.formFieldChanged(tabId, 'limit', clientLimit));
    const inputMode = limitOrderInputFormSelectors.getFormTradeSizeInputMode(state, tabId);
    if (clientLimit === undefined) {
      resetDefaultedFieldOnEmptyClientLimit(dispatch, inputMode, tabId);
    } else {
      computDefaultedFieldByInputMode(dispatch, inputMode, tabId, state);
    }
  };
}

export function salesMarginAdjustmentChangedThunks(tabId: string, salesMargin?: number): Thunk<void> {
  return (dispatch, getState) => {
    thunks.resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(limitOrderInputFormActions.formFieldChanged(tabId, 'salesMarginAdjustment', salesMargin));
  };
}

export function finishLimitOrderThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const closeTab = getUserPreferencesSecondaryMarketCloseTabWhenOrderPlacedActivated(state);
    const workspaceType = getTabWorkspaceType(state, tabId)! as SecondaryWorkspaceType;
    dispatch(limitOrderOrderActions.secondaryMarketOrderCanceled(tabId, workspaceType));
    if (closeTab === true) {
      dispatch(workspaceTabRemoved(tabId, workspaceType));
      dispatch(workspaceTabUnactivated(workspaceType));
    }
  };
}
const reloadFormFetchedThunk = (
  tabId: string,
  workspace: SecondaryWorkspaceType,
  response: ReloadFormResponse<LimitOrderReferentialFormResponse>,
): Thunk<void> => {
  return (dispatch, getState) => {
    const state = getState();
    const cashAmountByQuantity = computeCashAmountOnReferentialReceived(state, tabId, response);
    dispatch(limitOrderInputFormActions.reloadFormFetched(tabId, workspace, response));
    dispatch(limitOrderInputFormActions.formFieldChanged(tabId, 'cashAmount', cashAmountByQuantity, false));
  };
};

export const limitOrderThunks = {
  ...thunks,
  ...tabCreatedThunksFactory('LIMIT_ORDER', 'limitOrder', isinOrFileCode => `Limit order ${isinOrFileCode}`),
  amendRequestedThunk,
  reloadTabThunk,
  amendFetchedThunk,
  clientLimitChangedThunks,
  salesMarginAdjustmentChangedThunks,
  finishLimitOrderThunk,
  reloadFormFetchedThunk,
};

export type LimitOrderThunks = typeof limitOrderThunks;
function computeCashAmountOnReferentialReceived(
  state: AppState,
  tabId: string,
  response: ReloadFormResponse<LimitOrderReferentialFormResponse>,
) {
  const limit = limitOrderSelectors.getLimit(state, tabId);
  const quantity = limitOrderSelectors.getFormQuantity(state, tabId);
  const denomination = response.referential?.denomination;
  const priceType = response.referential?.priceType;
  const cashAmount = computeCashAmount(limit, denomination, priceType);
  const cashAmountByQuantity = quantity !== undefined && cashAmount !== undefined ? cashAmount * quantity : undefined;
  return cashAmountByQuantity;
}
